import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Settings, LogOut } from "lucide-react";
import { useUser } from "../foundation/altzero_base/base/contextapi/UserContext";
import { usePluginNavigation } from "../plugins/hooks";
import { useAdminAuth } from "../foundation/altzero_base/base/hooks/useAdminAuth";
import { usePermissions } from "../foundation/altzero_base/base/contextapi/PermissionsContext";
import { supabase } from "../foundation/altzero_base/base/utils/supabaseClient";

const Header = () => {
  const { user } = useUser();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 🆕 Get navigation from plugins
  const { navigation: pluginNavigation, isLoading } = usePluginNavigation();

  // Debug logging - Enhanced
  console.log("🔍 Header - Plugin navigation:", pluginNavigation);
  console.log("🔍 Header - Plugin navigation loading:", isLoading);
  console.log("🔍 Header - Plugin navigation length:", pluginNavigation.length);
  console.log(
    "🔍 Header - Plugin navigation items:",
    pluginNavigation.map((item) => ({ name: item.name, route: item.route }))
  );

  // 🆕 Check admin roles
  const { isAdmin: isSysAdmin } = useAdminAuth();
  const { isAdmin: isOrgAdmin } = usePermissions();
  const handleLogout = async () => {
    try {
      console.log("🚪 Logging out...");
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      console.log("✅ Logout successful, UserContext will handle navigation");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  // Check if the current route matches the link
  const isActive = (path: string) => {
    if (path === "/settings") {
      return (
        location.pathname.startsWith("/settings") ||
        location.pathname.startsWith("/organizations") ||
        location.pathname.startsWith("/teams") ||
        location.pathname.startsWith("/subscription")
      );
    }
    if (path === "/pseo") {
      return location.pathname.startsWith("/pseo");
    }
    if (path === "/copilot-chat") {
      return location.pathname.startsWith("/copilot-chat");
    }
    if (path === "/knowledge") {
      return location.pathname.startsWith("/knowledge");
    }
    if (path === "/admin") {
      return location.pathname.startsWith("/admin");
    }
    if (path === "/super-admin") {
      return location.pathname.startsWith("/super-admin");
    }
    if (path === "/formengine") {
      return location.pathname.startsWith("/formengine");
    }
    if (path === "/scopingai") {
      return location.pathname.startsWith("/scopingai");
    }
    if (path === "/bill-validation") {
      return location.pathname.startsWith("/bill-validation");
    }
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Combine static navigation with plugin navigation
  const staticNavigation = [];

  // Org admin sees /admin
  if (isOrgAdmin) {
    staticNavigation.push({ name: "Admin", route: "/admin", order: 11 });
  }
  // Sys admin (base_profiles.role === "admin") sees /super-admin
  if (isSysAdmin) {
    staticNavigation.push({ name: "Super Admin", route: "/super-admin", order: 12 });
  }

  // Merge and sort navigation
  const allNavigation = [...staticNavigation, ...pluginNavigation].sort(
    (a, b) => (a.order || 999) - (b.order || 999)
  );

  // Debug logging
  console.log("🔍 Header - All navigation:", allNavigation);

  return (
    <header className="app-header sticky top-0 z-40">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center">
              <img
                src="/choiceEnergyLogo.png"
                alt="Choice Energy Logo"
                className="w-20 object-contain"
              />
            </Link>

            <nav className="hidden md:block">
              <ul className="flex space-x-6">
                {user && (
                  <>
                    {allNavigation.map((item) => (
                      <li key={item.route}>
                        <Link
                          to={item.route}
                          className={
                            isActive(item.route)
                              ? "header-link-active"
                              : "header-link"
                          }
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </>
                )}
              </ul>
            </nav>
          </div>

          {user ? (
            <div className="hidden md:flex items-center space-x-3">
              {/* Settings Icon */}
              <Link
                to="/settings"
                className="p-2.5 rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 group shadow-sm hover:shadow"
                title="Settings"
              >
                <Settings className="w-5 h-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
              </Link>

              {/* Logout Icon */}
              <button
                onClick={handleLogout}
                className="p-2.5 rounded-lg bg-white border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-all duration-200 group shadow-sm hover:shadow"
                title="Logout"
              >
                <LogOut className="w-5 h-5 text-gray-600 group-hover:text-red-600 transition-colors" />
              </button>
            </div>
          ) : (
            <div className="hidden md:block">
              <Link
                to="/login"
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-standard"
              >
                Login
              </Link>
            </div>
          )}

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              className="text-primary-foreground focus:outline-none"
              onClick={toggleMobileMenu}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={
                    mobileMenuOpen
                      ? "M6 18L18 6M6 6l12 12" // X shape for close
                      : "M4 6h16M4 12h16M4 18h16" // Hamburger menu
                  }
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-primary-foreground border-t border-border">
          <div className="container mx-auto px-4 py-3">
            <nav>
              <ul className="space-y-3">
                {user ? (
                  <>
                    {allNavigation.map((item) => (
                      <li key={`mobile-${item.route}`}>
                        <Link
                          to={item.route}
                          className={`block py-2 px-3 rounded-md ${
                            isActive(item.route)
                              ? "bg-primary text-primary-foreground"
                              : "text-foreground hover:bg-muted"
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link
                        to="/settings"
                        className={`block py-2 px-3 rounded-md ${
                          isActive("/settings")
                            ? "bg-primary text-primary-foreground"
                            : "text-foreground hover:bg-muted"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Settings
                      </Link>
                    </li>
                    <li className="pt-2 border-t border-border">
                      <button
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="block w-full text-left py-2 px-3 rounded-md text-destructive hover:bg-muted"
                      >
                        Logout ({user.email})
                      </button>
                    </li>
                  </>
                ) : (
                  <li>
                    <Link
                      to="/login"
                      className="block py-2 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
